import React, { createContext, useContext, useState, useCallback, useMemo, useEffect } from "react";
import { initParityLogging, logConfigSnapshot } from "../utils/debugConfigParity";
import { useReportDocCollabSession } from "../../../../../utils/collab/useReportDocCollabSession";

export const ReportLandingContext = createContext();

export const ReportLandingContextProvider = ({ children, reportId, isPublicSnapshot, forceReadOnly }) => {

  // ── Collab (always-live) editing session ────────────────────────────────
  // Disabled for the public snapshot page and for forceReadOnly embeds (which
  // depend on the legacy fetched-config path so configTransform applies), and
  // behind FEATURES_DISABLED_ON_PROD + reports module access (see the hook).
  // When active, the report's config + meta come from the live Y.Doc and edits
  // route through per-widget setFields; editability is by access (PUBLISH) —
  // there is no edit URL. When inactive (prod / no access / public / forced read-only /
  // not synced), the legacy local-state + manual-Publish flow below is used
  // unchanged.
  const reportCollab = useReportDocCollabSession({
    reportId,
    disabled: isPublicSnapshot || forceReadOnly,
  });
  const collabActive = reportCollab.collabActive;


  const [loadedWidgetsTracker, setLoadedWidgetsTracker] = useState([]);

  // The widget currently taken into edit, tracked by its STABLE id (not array
  // index). The array index the ConfigForms consume is DERIVED from this id
  // against the live config below — so if the config reorders/shrinks under us
  // (a collab peer reordering or deleting), editing stays pointed at the right
  // widget instead of silently jumping to whatever now sits at the old index.
  const [underEditWidgetId, setUnderEditWidgetId] = useState(undefined);

  const [config, setConfig] = useState([]);

  // Payload-parity harness (inert unless localStorage.OOLOI_PICKER_PARITY set).
  useEffect(() => { initParityLogging(); }, []);
  useEffect(() => { logConfigSnapshot(config); }, [config]);

  const [ tempWidgetConfig, setTempWidgetConfig ] = useState({});
  
  const [activeFilters, setActiveFilters] = useState([]);

  const [readOnly, setReadOnly] = useState(false);

  // Report metadata state
  const [localReportMeta, setLocalReportMeta] = useState({ title: '', subtitle: '' });

  // Modal states
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  // Report data
  const [reportData, setReportData] = useState(null);

  // Widget data - stores each widget's fetched API response data keyed by widget id.
  // Read by chart config forms (to build data-driven controls) and by report
  // snapshotting (to capture what every widget rendered). See SHARED_REPORTS_DESIGN.md.
  const [widgetDataStore, setWidgetDataStore] = useState({});

  const updateWidgetDataStore = useCallback((widgetId, data) => {
    setWidgetDataStore(prev => {
      const existing = prev[widgetId];
      const dataChanged = !existing || JSON.stringify(existing) !== JSON.stringify(data);
      if (!dataChanged) return prev; // Return same reference if data unchanged
      return { ...prev, [widgetId]: data };
    });
  }, []);

  // Clear a widget's cached data. Used on a dimension change so data-driven config
  // controls (e.g. the Show/Hide bar-value list) don't keep reading the PREVIOUS
  // dimension's result. The store is only overwritten on a successful, non-empty
  // fetch, so without this a stale slice would persist through the loading window
  // (and permanently if the new dimension's result is empty).
  const clearWidgetDataStore = useCallback((widgetId) => {
    setWidgetDataStore(prev => {
      if (!(widgetId in prev)) return prev; // nothing cached → same reference
      const next = { ...prev };
      delete next[widgetId];
      return next;
    });
  }, []);

  // Public-share snapshot replay: when a report is opened via a public share link, the
  // captured { [widgetId]: data } blob is put here. WidgetRenderer feeds each widget its
  // slice as `prefetchedData`, so widgets render frozen data and skip their own fetch.
  // null in normal (live) mode. See SHARED_REPORTS_DESIGN.md.
  const [snapshotData, setSnapshotData] = useState(null);

  // True ONLY when this report is rendered as a public "share for all to view" page.
  // Distinct from `snapshotData`/`prefetchedData` (which is the generic "render from given
  // data, skip fetching" mechanism, potentially reused for non-public purposes). Use this
  // to gate public-specific UI — hiding references, AI-summary/regenerate controls, etc.
  // Seeded from the prop, not left false until ReportMainPage's effect sets it:
  // child effects flush before the parent's, so a false first commit let public
  // pages fire requests that are gated on this.
  const [isPublicReport, setIsPublicReport] = useState(!!isPublicSnapshot);

  // Widget time range options - stores dynamic time range options keyed by widget index
  const [widgetTimeRangeOptions, setWidgetTimeRangeOptions] = useState({});
  const updateWidgetTimeRangeOptions = useCallback((widgetIdx, options, dataSpanDays) => {
    setWidgetTimeRangeOptions(prev => {
      // Prevent unnecessary state updates if values haven't changed
      const existing = prev[widgetIdx];
      if (
        existing &&
        existing.dataSpanDays === dataSpanDays &&
        JSON.stringify(existing.options) === JSON.stringify(options)
      ) {
        return prev; // Return same reference to prevent re-render
      }
      return {
        ...prev,
        [widgetIdx]: { options, dataSpanDays }
      };
    });
  }, []);

  // Effective state: swap in the live Y.Doc values + collab writers when a
  // collab session is active; otherwise pass the legacy local state through.
  // Every consumer (ConfigForms, ReportMainPage, WidgetRenderer, header) reads
  // these, so the swap is transparent — index-based setConfig calls get diffed
  // into per-widget setFields by applyConfig.
  const effectiveConfig = collabActive ? (reportCollab.liveConfig || []) : config;
  // In collab, route writes to the targeted per-widget applyConfig. The write
  // is scoped to ONE widget's entry, so applyConfig must know which widget
  // changed: callers that edit a widget on the canvas (the summary→text
  // convert — anything outside the under-edit flow) pass its id explicitly as
  // the second arg; config-form edits omit it and fall back to the under-edit
  // id, which for them is by definition the widget being edited. Guessing here
  // (e.g. from under-edit alone) is what silently discarded on-canvas edits:
  // the id was undefined at write time and applyConfig dropped the change.
  // (Structural add/remove/reorder is detected inside applyConfig by order
  // diff and doesn't need an id.)
  const effectiveSetConfig = useCallback(
    (next, changedWidgetId) =>
      collabActive
        ? reportCollab.applyConfig(next, changedWidgetId ?? underEditWidgetId)
        : setConfig(next),
    [collabActive, reportCollab, underEditWidgetId, setConfig]
  );

  const effectiveMeta = collabActive
    ? (reportCollab.liveMeta || { title: "", subtitle: "" })
    : localReportMeta;
  // Takes a PARTIAL ({ title } or { subtitle }): callers send only the key
  // they edited. In collab that becomes a single setField write; spreading a
  // liveMeta snapshot here would re-send the other key too, and a stale copy
  // of it can overwrite a peer's newer, locked edit.
  const effectiveSetMeta = useCallback(
    (partial) => {
      if (!collabActive) return setLocalReportMeta((prev) => ({ ...prev, ...partial }));
      reportCollab.setMeta(partial);
    },
    [collabActive, reportCollab, setLocalReportMeta]
  );
  // In collab mode editability is by access (PUBLISH), not the URL mode.
  // forceReadOnly wins over both — the embed contract is "never editable".
  // The collabPending branch is the connecting window: NOT the same as "collab
  // is off". Falling through to `readOnly` there would render an editable page
  // on /edit whose keystrokes land in the local state below and get discarded
  // when the Y.Doc becomes the source — so it's held read-only instead. (The
  // loading gate in ReportLanding means this state is normally never rendered;
  // this is the backstop.)
  const effectiveReadOnly = forceReadOnly
    ? true
    : collabActive
    ? !reportCollab.canEdit
    : reportCollab.collabPending
    ? true
    : readOnly;

  // Array index of the under-edit widget, resolved from its stable id against the
  // live config. undefined when nothing is under edit (or the widget vanished).
  // ConfigForms + WidgetRenderer read this; it re-resolves on every config change.
  const underEditWidgetIdx = useMemo(() => {
    if (underEditWidgetId == null) return undefined;
    const i = effectiveConfig.findIndex((w) => w?.id === underEditWidgetId);
    return i === -1 ? undefined : i;
  }, [effectiveConfig, underEditWidgetId]);

  // Hold a collab field lock on the widget being edited (keyed by its stable id),
  // so peers see it as locked and are blocked from editing the same widget.
  // Acquired when a widget goes under-edit, released when edit moves on / unmounts.
  const { acquireLock, releaseLock } = reportCollab;
  useEffect(() => {
    if (!collabActive || !underEditWidgetId) return undefined;
    const valuePath = `config.${underEditWidgetId}`;
    acquireLock(valuePath);
    return () => releaseLock(valuePath);
  }, [collabActive, underEditWidgetId, acquireLock, releaseLock]);

  const contextValue = useMemo(() => {
    return {
      loadedWidgetsTracker,
      setLoadedWidgetsTracker,

      underEditWidgetIdx,
      underEditWidgetId,
      setUnderEditWidgetId,

      config: effectiveConfig,
      setConfig: effectiveSetConfig,

      activeFilters,
      setActiveFilters,

      readOnly: effectiveReadOnly,
      setReadOnly,

      // Collab (always-live) session — null-ish members when inactive
      collabActive,
      collabPending: reportCollab.collabPending,
      reportCollab,

      // Report metadata
      localReportMeta: effectiveMeta,
      setLocalReportMeta: effectiveSetMeta,

      // Modal states
      showDeleteModal,
      setShowDeleteModal,

      // Report data
      reportData,
      setReportData,

      // Temp widget config
      tempWidgetConfig,
      setTempWidgetConfig,

      // Widget data (shared between widget render, config forms, and share-snapshotting)
      widgetDataStore,
      updateWidgetDataStore,
      clearWidgetDataStore,

      // Public-share snapshot replay data
      snapshotData,
      setSnapshotData,

      // Public-report mode flag (gates public-specific UI)
      isPublicReport,
      setIsPublicReport,

      // Widget time range options (for dynamic time range dropdowns)
      widgetTimeRangeOptions,
      updateWidgetTimeRangeOptions
    };
  }, [
    loadedWidgetsTracker,
    underEditWidgetIdx,
    underEditWidgetId,
    effectiveConfig,
    effectiveSetConfig,
    activeFilters,
    effectiveReadOnly,
    collabActive,
    reportCollab,
    reportCollab.collabPending,
    effectiveMeta,
    effectiveSetMeta,
    showDeleteModal,
    reportData,
    tempWidgetConfig,
    widgetDataStore,
    updateWidgetDataStore,
    clearWidgetDataStore,
    snapshotData,
    setSnapshotData,
    isPublicReport,
    setIsPublicReport,
    widgetTimeRangeOptions,
    updateWidgetTimeRangeOptions
  ]);

  return (
    <ReportLandingContext.Provider value={contextValue}>
      {children}
    </ReportLandingContext.Provider>
  );
};

export const useReportLandingContext = () => {
  return useContext(ReportLandingContext);
};

export const useWidgetEditing = () => {
  const { underEditWidgetIdx, underEditWidgetId, setUnderEditWidgetId } = useReportLandingContext();
  return { underEditWidgetIdx, underEditWidgetId, setUnderEditWidgetId };
};

export const useReportConfig = () => {
  const { config, setConfig } = useReportLandingContext();
  return { config, setConfig };
};

/**
 * The live collab session for this report (always-live editing). `collabActive`
 * is false whenever collab is off (prod / no access / public / not synced), in
 * which case the legacy local-state + manual-Publish flow is in effect and
 * `reportCollab`'s edit members are unused.
 */
export const useReportCollab = () => {
  const { collabActive, collabPending, reportCollab } = useReportLandingContext();
  return { collabActive, collabPending, reportCollab };
};

export const useReportFilters = () => {
  const { activeFilters, setActiveFilters } = useReportLandingContext();
  return { activeFilters, setActiveFilters };
};

export const useReportMode = () => {
  const { readOnly, setReadOnly } = useReportLandingContext();
  return { readOnly, setReadOnly };
};

export const useReportMeta = () => {
  const { localReportMeta, setLocalReportMeta } = useReportLandingContext();
  return { localReportMeta, setLocalReportMeta };
};

export const useReportModals = () => {
  const { showDeleteModal, setShowDeleteModal } = useReportLandingContext();
  return { showDeleteModal, setShowDeleteModal };
};

export const useReportData = () => {
  const {
    reportData,
    setReportData,
  } = useReportLandingContext();
  return {
    reportData,
    setReportData,
  };
};

// Public-share snapshot: replay data (for the public read-only page) + the live
// capture sources (widgetDataStore = what each widget rendered, loadedWidgetsTracker = which
// widgets have finished). See SHARED_REPORTS_DESIGN.md.
export const useReportSnapshot = () => {
  const {
    snapshotData,
    setSnapshotData,
    isPublicReport,
    setIsPublicReport,
    widgetDataStore,
    loadedWidgetsTracker,
  } = useReportLandingContext();
  return { snapshotData, setSnapshotData, isPublicReport, setIsPublicReport, widgetDataStore, loadedWidgetsTracker };
};
